import React, { useState, useEffect, useCallback } from "react";
import { X, CreditCard as CreditCardIcon } from "lucide-react";
// import PayPalPaymentComponent from "./PayPalPaymentComponent";

const PaymentMethodModal = ({ isOpen, onClose, onStripePayment, quote, totalAmount, sizePrices }) => {
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'stripe' ou 'paypal'
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [showPayPalModal, setShowPayPalModal] = useState(false);

  // Helper function to safely parse sizes_quantities
  const parseSizesQuantities = (sizesData) => {
    if (!sizesData) return {};
    
    // If it's already an object and not an array, return it
    if (typeof sizesData === 'object' && !Array.isArray(sizesData)) {
      return sizesData;
    }
    
    // If it's a string, try to parse it as JSON
    if (typeof sizesData === 'string') {
      try {
        const parsed = JSON.parse(sizesData);
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing sizes_quantities string:', e);
      }
    }
    
    // If it's an array or other format, return empty object
    console.warn('sizes_quantities is not in expected format:', sizesData);
    return {};
  };

  // Calcul du total - stable et ne change plus une fois calculé
  const calculateTotal = useCallback(() => {
    // Si totalAmount est fourni, l'utiliser en priorité
    if (totalAmount && totalAmount > 0) {
      setCalculatedTotal(totalAmount);
      return;
    }

    let total = 0;
    let quoteData = quote;
    let totalQuantity = 0;
    
    console.log("PaymentMethodModal - calculateTotal:", {
      quoteData,
      sizes_quantities: quoteData?.sizes_quantities,
      sizes_quantities_type: typeof quoteData?.sizes_quantities,
      sizePrices
    });

    if (!quoteData) {
      setCalculatedTotal(0);
      return;
    }

    if (quoteData.total && !isNaN(parseFloat(quoteData.total))) {
      total = parseFloat(quoteData.total);
    } else if (quoteData.sizes_quantities && sizePrices && Object.keys(sizePrices).length > 0) {
      // Use the helper function to safely parse sizes_quantities
      const parsedSizes = parseSizesQuantities(quoteData.sizes_quantities);
      
      if (Object.keys(parsedSizes).length > 0) {
        for (const [sizeName, quantity] of Object.entries(parsedSizes)) {
          const qty = parseInt(quantity) || 0;
          const price = !isNaN(sizePrices[sizeName]) ? sizePrices[sizeName] : 0;
          
          total += price * qty;
          totalQuantity += qty;
        }
      }
    } else if (quoteData.quantity && !isNaN(parseInt(quoteData.quantity))) {
      // Fallback to simple quantity if sizes_quantities is not available
      const qty = parseInt(quoteData.quantity);
      const sizeValues = sizePrices && typeof sizePrices === 'object' ? Object.values(sizePrices) : [];
      const avgPrice = sizeValues.length > 0 
        ? sizeValues.reduce((sum, price) => sum + price, 0) / sizeValues.length 
        : 0;
      total = avgPrice * qty;
      totalQuantity = qty;
    } else {
      total = quoteData.unit_price || quoteData.base_price || 0;
    }

    // Add logo price if applicable
    const logoPrice = parseFloat(quoteData.price_logo_quote);
    const shouldAddLogoPrice =
      quoteData.is_logo_required &&
      !isNaN(logoPrice) &&
      logoPrice > 0;

    if (shouldAddLogoPrice) {
      total += logoPrice; // Add logo price once, not multiplied by quantity
    }

    setCalculatedTotal(total);
  }, [quote, sizePrices, totalAmount]);

  // Initialisation au chargement - une seule fois
  useEffect(() => {
    if (isOpen && !showPayPalModal) {
      setPaymentMethod(null);
      calculateTotal();
    }
  }, [isOpen, calculateTotal, showPayPalModal]);

  // Reset quand le modal se ferme complètement
  useEffect(() => {
    if (!isOpen) {
      setPaymentMethod(null);
      setShowPayPalModal(false);
      setLoading(false);
    }
  }, [isOpen]);

  // Gestion du clic sur Stripe
  const handleStripeClick = () => {
    setPaymentMethod('stripe');
    if (onStripePayment) {
      onStripePayment();
    }
  };

  // Gestion du clic sur PayPal
  const handlePayPalClick = () => {
    setPaymentMethod('paypal');
    setShowPayPalModal(true);
  };

  // Fermer le modal PayPal et revenir au choix des méthodes
  const handleClosePayPalModal = () => {
    setShowPayPalModal(false);
    setPaymentMethod(null);
    // Ne pas fermer le modal principal, juste revenir à la sélection
  };

  // Retour depuis PayPal vers le choix des méthodes
  const handleBackFromPayPal = () => {
    setShowPayPalModal(false);
    setPaymentMethod(null);
    // Rester dans le modal principal pour permettre une nouvelle sélection
  };

  // Fermer complètement le modal
  const handleClose = () => {
    if (!loading) {
      setPaymentMethod(null);
      setShowPayPalModal(false);
      onClose();
    }
  };

  // Gérer la réussite du paiement PayPal
  const handlePayPalSuccess = () => {
    // Fermer tout et retourner au parent
    setShowPayPalModal(false);
    setPaymentMethod(null);
    onClose();
  };

  if (!isOpen) return null;

  // Si PayPal est sélectionné, afficher le modal PayPal
  // if (showPayPalModal) {
  //   return (
  //     <PayPalPaymentComponent
  //       isOpen={showPayPalModal}
  //       onClose={handleClosePayPalModal}
  //       onSuccess={handlePayPalSuccess}
  //       quote={quote}
  //       totalAmount={totalAmount}
  //       sizePrices={sizePrices}
  //       onBack={handleBackFromPayPal}
  //     />
  //   );
  // }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Choisir une méthode de paiement
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {/* Affichage du montant */}
          {calculatedTotal > 0 && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md">
              <div className="text-sm">
                 Montant à payer : <span className="font-semibold">{calculatedTotal.toFixed(2)} $  </span>
              </div>
            </div>
          )}

          {/* Sélection des méthodes de paiement */}
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={handleStripeClick}
              disabled={loading}
              className="flex items-center justify-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CreditCardIcon size={24} className="text-blue-600" />
              <span className="font-medium">Payer avec Stripe</span>
            </button>
          </div>

          <div className="mt-4 text-xs text-gray-500 text-center">
            Toutes les transactions sont sécurisées et chiffrées
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;